import React, { useState, useEffect, useCallback, useRef, Suspense, lazy } from 'react';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import BreakingNewsTicker from '../components/BreakingNewsTicker';
import AdSlot from '../components/AdSlot';
import Meta from '../components/Meta';
import { useLanguage } from '../context/LanguageContext';
import { TrendingUp, Loader2 } from 'lucide-react';

import FeaturedHeroSection from '../components/home/FeaturedHeroSection';
import ThreeColumnPanel from '../components/home/ThreeColumnPanel';
import LatestStoriesSection from '../components/home/LatestStoriesSection';
import CategoryShowcase from '../components/home/CategoryShowcase';
import StoryCard from '../components/home/StoryCard';
import SidebarSection from '../components/home/SidebarSection';

const API = process.env.REACT_APP_BACKEND_URL;

const CroatiaMapWidget = lazy(() => import('../components/CroatiaMapWidget'));
const TimesBrandsWidget = lazy(() => import('../components/TimesBrandsWidget'));

/* ─── IntersectionObserver lazy section ─────────────────────────────── */
function LazySection({ children, fallback, rootMargin = '200px' }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { rootMargin }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [rootMargin]);
  return <div ref={ref}>{visible ? children : (fallback || <div className="h-32" />)}</div>;
}

/* ═══ MAIN HOMEPAGE ═══════════════════════════════════════════════════ */
export default function HomePage() {
  const { lang, t } = useLanguage();
  const [articles, setArticles] = useState([]);
  const [popularArticles, setPopularArticles] = useState([]);
  const [tags, setTags] = useState([]);
  const [topCategories, setTopCategories] = useState([]);
  const [categoryArticles, setCategoryArticles] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [widgetSettings, setWidgetSettings] = useState({});
  const [catalogues, setCatalogues] = useState([]);
  const [featuredJobs, setFeaturedJobs] = useState([]);
  const [featuredClassifieds, setFeaturedClassifieds] = useState([]);

  // Hero data from bundle (passed to FeaturedHeroSection)
  const [heroData, setHeroData] = useState(null);

  useEffect(() => {
    loadHomeData();
    const sessionId = sessionStorage.getItem('ct_session') || (() => { const sid = Math.random().toString(36).slice(2); sessionStorage.setItem('ct_session', sid); return sid; })();
    axios.post(`${API}/api/analytics/track`, { session_id: sessionId, url: '/', page_title: 'Homepage' }).catch(() => {});
  }, [lang]);

  const loadHomeData = async (retryCount = 0) => {
    const MAX_RETRIES = 2;
    try {
      setLoadError(false);
      // Single bundled API call replaces 8+ concurrent requests
      const bundleRes = await axios.get(`${API}/api/homepage/bundle?lang=${lang}`, { timeout: 15000 });
      const bundle = bundleRes.data || {};

      const allArticles = bundle.articles || [];
      const popular = bundle.popular_articles || [];
      const tagData = bundle.tags || [];
      const ws = bundle.widget_settings || {};
      const topCats = bundle.top_categories || [];

      // Set hero data for FeaturedHeroSection
      setHeroData({
        heroArticles: bundle.hero_articles || [],
        latestArticles: bundle.latest_sidebar || [],
        mostReadArticles: bundle.most_read_sidebar || [],
      });

      setArticles(allArticles);
      setPopularArticles(popular);
      setTags(tagData);
      setWidgetSettings(ws);
      setTopCategories(topCats);

      const byCategory = {};
      for (const cat of topCats) {
        const catName = cat.category;
        byCategory[catName] = allArticles.filter(
          a => a.category?.toLowerCase() === catName.toLowerCase()
        ).slice(0, 6);
      }
      setCategoryArticles(byCategory);

      // Load optional widget data sequentially (non-critical)
      const widgetPromises = [];
      if (ws.pdf_catalogues_enabled) widgetPromises.push(axios.get(`${API}/api/catalogues`).then(r => setCatalogues(r.data?.catalogues || [])).catch(() => {}));
      if (ws.featured_jobs_enabled) widgetPromises.push(axios.get(`${API}/api/featured-jobs`).then(r => setFeaturedJobs(r.data?.jobs || [])).catch(() => {}));
      if (ws.featured_classifieds_enabled) widgetPromises.push(axios.get(`${API}/api/featured-classifieds`).then(r => setFeaturedClassifieds(r.data?.classifieds || [])).catch(() => {}));
      await Promise.all(widgetPromises);
    } catch (err) {
      console.error('Failed to load homepage:', err);
      if (retryCount < MAX_RETRIES) {
        // Auto-retry after a short delay
        await new Promise(r => setTimeout(r, 1500 * (retryCount + 1)));
        return loadHomeData(retryCount + 1);
      }
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const res = await axios.get(`${API}/api/articles?limit=12&skip=${articles.length}&status=published&lang=${lang}`);
      const more = res.data?.articles || res.data || [];
      if (more.length === 0) setHasMore(false);
      else setArticles(prev => [...prev, ...more]);
    } catch {}
    setLoadingMore(false);
  }, [articles.length, loadingMore, hasMore, lang]);

  const latestArticles = articles.slice(1, 19);
  const moreArticles = articles.slice(19);

  return (
    <div className="min-h-screen bg-cream dark:bg-navy overflow-x-hidden" data-testid="homepage">
      <Meta title={null} description={t('footer_description')} />
      <Header />
      <BreakingNewsTicker />

      {widgetSettings.header_ads_enabled && (
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <AdSlot width="970" height="90" slot="leaderboard-top" className="mx-auto" />
        </div>
      )}

      <main className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        {loadError && !loading && articles.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 gap-4" data-testid="homepage-error">
            <p className="text-gray-500 dark:text-gray-400 text-sm">Došlo je do greške pri učitavanju sadržaja.</p>
            <button 
              onClick={() => { setLoading(true); loadHomeData(); }} 
              className="px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-lg transition-colors"
              data-testid="homepage-retry-btn"
            >
              Pokušaj ponovo
            </button>
          </div>
        )}
        <FeaturedHeroSection loading={loading} t={t} bundledData={heroData} />

        {/* Mobile-only Times Portali panel (before Trendovi) */}
        {(!widgetSettings.hide_mobile_top_portals) && (
          <div className="lg:hidden my-4" data-testid="mobile-top-portals">
            <Suspense fallback={<div className="h-20 bg-gray-100 dark:bg-navy-light rounded-2xl animate-pulse" />}>
              <TimesBrandsWidget variant="full" />
            </Suspense>
          </div>
        )}

        <ThreeColumnPanel categories={topCategories} t={t} />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Main Content (8/12) */}
          <div className="lg:col-span-8 space-y-8">
            <LatestStoriesSection articles={latestArticles} loading={loading} t={t} />
            <AdSlot width="728" height="90" slot="in-feed-1" className="mx-auto" />

            {/* Category Showcases */}
            {topCategories.slice(0, 5).map((cat, idx) => {
              const catArts = categoryArticles[cat.category] || [];
              if (catArts.length === 0) return null;
              return (
                <LazySection key={cat.slug} rootMargin="300px">
                  <CategoryShowcase category={cat.category} articles={catArts} index={idx} t={t} />
                  {idx === 1 && <AdSlot width="300" height="250" slot={`in-feed-native-${idx}`} className="mx-auto sm:hidden mt-6" />}
                </LazySection>
              );
            })}

            {/* Times Portals */}
            <LazySection rootMargin="300px" fallback={<div className="h-32" />}>
              <section data-testid="homepage-portals-middle" className="my-8">
                <Suspense fallback={<div className="h-32 bg-gray-100 dark:bg-navy-light rounded-2xl animate-pulse" />}>
                  <TimesBrandsWidget variant="full" />
                </Suspense>
              </section>
            </LazySection>

            {/* Remaining Categories */}
            {topCategories.slice(5, 10).map((cat, idx) => {
              const catArts = categoryArticles[cat.category] || [];
              if (catArts.length === 0) return null;
              return (
                <LazySection key={cat.slug} rootMargin="300px">
                  <CategoryShowcase category={cat.category} articles={catArts} index={idx + 5} t={t} />
                </LazySection>
              );
            })}

            {/* More Stories */}
            {moreArticles.length > 0 && (
              <LazySection rootMargin="300px">
                <section data-testid="more-stories">
                  <div className="flex items-center gap-2 mb-5">
                    <TrendingUp size={18} className="text-sky-accent" />
                    <h2 className="font-serif text-xl font-bold text-navy dark:text-white">{t('more')}</h2>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {moreArticles.map((a, i) => <StoryCard key={a.id || a._id || i} article={a} compact />)}
                  </div>
                  {hasMore && (
                    <div className="flex justify-center mt-6">
                      <button onClick={loadMore} disabled={loadingMore} data-testid="load-more-btn" className="px-6 py-2.5 bg-navy dark:bg-white/10 text-white rounded-full text-sm font-display font-semibold hover:bg-navy-light transition-colors disabled:opacity-50 flex items-center gap-2">
                        {loadingMore ? <><Loader2 size={14} className="animate-spin" /> {t('loading')}</> : t('load_more')}
                      </button>
                    </div>
                  )}
                </section>
              </LazySection>
            )}

            <AdSlot width="728" height="90" slot="bottom-leaderboard" className="mx-auto" />
          </div>

          {/* Sidebar (4/12) */}
          <SidebarSection
            popularArticles={popularArticles}
            articles={articles}
            tags={tags}
            widgetSettings={widgetSettings}
            catalogues={catalogues}
            featuredJobs={featuredJobs}
            featuredClassifieds={featuredClassifieds}
            t={t}
          />
        </div>
      </main>

      <LazySection rootMargin="300px" fallback={<div className="h-64" />}>
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
          <Suspense fallback={<div className="h-64 bg-gray-100 dark:bg-navy-light rounded-2xl animate-pulse" />}>
            <CroatiaMapWidget />
          </Suspense>
        </div>
      </LazySection>

      <Footer />
    </div>
  );
}
